import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Music24PlanCapabilities, SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { featureCopy, featureRowValue } from './upgradeMessages';

export interface UpgradeCopy {
  label: string;
  title: string;
  lede: string;
}

interface UseUpgradeCopyParams {
  feature: SMLUpgradeFeature;
  subject?: string;
  planName: string;
  monthlyPrice: string;
  /** Inside a native app's web view (App Store 3.1.1): the lede quotes no web price. */
  hidePrice?: boolean;
  planCapabilities: Music24PlanCapabilities;
  currentCapabilities: Music24PlanCapabilities;
}

/** Turns the trigger context into the words on the modal: the title and the body. */
export function useUpgradeCopy({
  feature,
  subject,
  planName,
  monthlyPrice,
  hidePrice = false,
  planCapabilities,
  currentCapabilities,
}: UseUpgradeCopyParams): UpgradeCopy {
  const intl = useIntl();

  return useMemo(() => {
    const isHistory = feature === SMLUpgradeFeature.history;
    const isDepth = feature === SMLUpgradeFeature.browseDepth;
    const isFollow = feature === SMLUpgradeFeature.followLimit;

    let current = 0;
    let target = 0;
    if (isHistory) {
      current = currentCapabilities.historyDaysLimit;
      target = planCapabilities.historyDaysLimit;
    } else if (isDepth) {
      current = currentCapabilities.maxOffset;
      target = planCapabilities.maxOffset;
    } else if (isFollow) {
      current = currentCapabilities.artistsFollowLimit;
      target = planCapabilities.artistsFollowLimit;
    }
    const extra = Math.max(target - current, 0);

    // The 13 free-plans features quote the plan's own numbers in prose (e.g. "Your published-page
    // limit is 3. Artist's is Unlimited.") instead of re-deriving "Unlimited"/"a month" formatting
    // here — `featureRowValue` is the same formatter the plans window's compare table uses.
    const currentText = featureRowValue(feature, currentCapabilities, intl);
    const targetText = featureRowValue(feature, planCapabilities, intl);

    const values = {
      subject: subject ?? '',
      plan: planName,
      price: monthlyPrice,
      days: planCapabilities.historyDaysLimit,
      count: planCapabilities.maxOffset,
      artists: planCapabilities.artistsFollowLimit,
      tracks: planCapabilities.tracksFollowLimit,
      // The X prompt: the X posts a month of one X account on the offered plan.
      posts: planCapabilities.xPostsPerMonth,
      current,
      target,
      extra,
      currentText,
      targetText,
    };

    const copy = featureCopy[feature];

    return {
      label: intl.formatMessage(copy.label),
      title: intl.formatMessage(subject === undefined ? copy.title : copy.titleWithSubject, values),
      lede: intl.formatMessage(hidePrice ? (copy.ledeWithoutPrice ?? copy.lede) : copy.lede, values),
    };
  }, [intl, feature, subject, planName, monthlyPrice, hidePrice, planCapabilities, currentCapabilities]);
}
